import SignInSignUpForm, {
	SignInSignUpFormMode,
} from '@/components/SignInSignUpForm';
import api from '@/lib/api';
import _ from 'lodash';
import { Formik } from 'formik';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { object, string } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { TbInfoCircle } from 'react-icons/tb';

export default function Signin() {
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [mode, setMode] = React.useState<'signIn' | 'magic'>('signIn');
	const [magicLinkSent, setMagicLinkSent] = React.useState(false);
	const signinFormSchema = object({
		email: string().email('Please enter a valid email'),
		password: string(),
		// Password isn't schema-required: the magic-link branch submits with
		// email only. The password branch enforces it via setFieldError.
	}).required({ email: true });
	const [searchParams] = useSearchParams();

	const clientId = searchParams.get('client_id');
	const redirectUri = searchParams.get('redirect_uri');
	const appName = searchParams.get('appName');
	const state = searchParams.get('state');

	const switchMode = (next: SignInSignUpFormMode) => {
		setMode(next === 'magic' ? 'magic' : 'signIn');
		setMagicLinkSent(false); // a stale "sent" state must not survive a toggle
	};

	return (
		<>
			<Formik
				initialValues={{ email: '', password: '' }}
				onSubmit={async (values, { setFieldError }) => {
					setIsSubmitting(true);
					try {
						if (mode === 'magic') {
							// No retries: a retried send would email the link
							// several times and burn the rate limit.
							await api.post(
								'/auth/send-magic-link',
								{ email: values.email },
								{
									['axios-retry']: { retries: 0 },
									params: {
										clientId,
										redirectUri,
										...(state && { state }),
									},
								}
							);
							setMagicLinkSent(true);
							setIsSubmitting(false);
							return;
						}
						if (!values.password) {
							setFieldError('password', 'Password is required');
							setIsSubmitting(false);
							return;
						}
						const response = await api.post('/auth/login', values, {
							params: {
								clientId,
								redirectUri,
								...(state && { state }), // Include state if present
							},
						});
						if (response.data.urlToRedirect) {
							// Keep the button disabled through the redirect,
							// like the original page did.
							window.location.href = response.data.urlToRedirect;
						}
					} catch (err: any) {
						const msg =
							`${err}`.split(':')?.[1]?.trim() ||
							'Something went wrong';
						toast.error(_.startCase(msg));
						setIsSubmitting(false);
					}
				}}
				validationSchema={toFormikValidationSchema(signinFormSchema)}
			>
				{({
					values,
					errors,
					touched,
					handleChange,
					handleBlur,
					handleSubmit,
					setFieldError,
				}) => (
					<SignInSignUpForm
						authUrl={'/signup?' + searchParams.toString()}
						values={values}
						handleChange={handleChange}
						handleBlur={handleBlur}
						handleSubmit={handleSubmit}
						mode={mode}
						setMode={switchMode}
						isSubmitting={isSubmitting}
						errors={errors}
						touched={touched}
						setFieldError={setFieldError}
						magicLinkSent={magicLinkSent}
					/>
				)}
			</Formik>
			{appName === 'shortStop' && <div className='fixed bottom-10 right-10 z-20 flex max-w-[600px] items-start gap-3 rounded-lg border border-border/50 bg-card p-4 text-sm text-foreground shadow-card'>
				<div className="py-0.5">
					<TbInfoCircle size={25} />
				</div>
				<div className='flex-1'>This account allows you preserve your limits and settings on all your devices, so you don't have to recreate them on a new device when you need them.</div>
			</div>}
		</>
	);
}
